import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/** Natural-language search bar: sends free text to /api/search/ai and merges returned
 * structured filters into the parent's filters state. Degrades gracefully (empty
 * filters) if the AI backend is unavailable. */
export default function AiSearchBar({ searchType, onApplyFilters, testIdPrefix }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setFeedback(null);
    try {
      const { data } = await api.post("/search/ai", { query, search_type: searchType });
      const filters = data.filters || {};
      if (Object.keys(filters).length === 0) {
        setFeedback(t("search.aiNoFiltersFound"));
      } else {
        onApplyFilters(filters);
        setFeedback(t("search.aiFiltersApplied"));
      }
    } catch (e) {
      setFeedback(t("search.aiUnavailable"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSearch} className="mb-4" data-testid={`${testIdPrefix}-form`}>
      <div className="flex items-center gap-2 bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-2 pl-4">
        <Sparkles className="h-4 w-4 text-[#4ba3b7] shrink-0" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t(`search.${searchType === "offers" ? "aiPlaceholderOffers" : "aiPlaceholderCandidates"}`)}
          className="border-0 shadow-none focus-visible:ring-0 h-10 px-0"
          data-testid={`${testIdPrefix}-input`}
        />
        <Button type="submit" disabled={loading || !query.trim()} className="h-10 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] shrink-0" data-testid={`${testIdPrefix}-submit-button`}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("search.aiSearchButton")}
        </Button>
      </div>
      {feedback && <p className="text-xs text-[#2B2B2B]/50 mt-2 px-2" data-testid={`${testIdPrefix}-feedback`}>{feedback}</p>}
    </form>
  );
}
